import { Injectable, Injector, inject } from '@angular/core';
import type { ChangeRecord } from '@checkpoint/shared';
import { DatabaseService } from './database.service';
import { LoggerService } from './logger.service';

/**
 * Applies changes received from server
 * Implements last-write-wins conflict resolution
 */
@Injectable({
  providedIn: 'root',
})
export class ChangeApplierService {
  private db = inject(DatabaseService);
  private log = inject(LoggerService).getLogger('change-applier');
  private injector = inject(Injector);

  // CustomSyncService reference for broadcasting (injected lazily to avoid circular dependency)
  private syncService?: any;
  private hasUpdatedAtColumnCache = new Map<string, boolean>();

  /**
   * Apply schema DDL from server (initial sync only)
   */
  async applySchema(ddl: string): Promise<void> {
    const startTime = performance.now();
    this.log.debug(`📋 applySchema() called - DDL length: ${ddl.length} bytes`);
    try {
      this.log.debug(`🔨 Calling db.exec() with DDL...`);
      await this.db.exec(ddl);
      const elapsed = (performance.now() - startTime).toFixed(2);
      this.log.debug(`✅ db.exec() returned successfully (${elapsed}ms)`);
      this.log.info('[SCHEMA] Applied schema DDL from backend');

      // 🔍 DIAGNOSTIC: Verify schema was applied
      const tables = await this.db.exec(
        `SELECT name FROM sqlite_master WHERE type='table' AND name='permission_keys'`,
      );
      this.log.debug('🔍 After schema: permission_keys exists?', tables);

      if (tables && tables.length > 0) {
        const columns = await this.db.exec(`PRAGMA table_info(permission_keys)`);
        this.log.debug('🔍 permission_keys columns:', JSON.stringify(columns));
      }
    } catch (error: any) {
      this.log.error('[SCHEMA] Failed to apply schema:', error);
      throw error;
    }
  }

  /**
   * Apply a batch of changes from server
   */
  async applyChanges(changes: ChangeRecord[]): Promise<number> {
    const startTime = performance.now();
    let appliedCount = 0;
    const changesByTable = new Map<string, { operation: string; rowIds: string[] }>();

    // One transaction for the whole batch: without it every row is its own
    // autocommit write to OPFS, which made a first sync of ~600 rows take ~7s
    // on Android. Per-row failures are still caught in applyChange() (SQLite
    // rolls back only the failing statement), so the batch keeps going.
    const useTransaction = changes.length > 1;
    if (useTransaction) {
      await this.db.exec('BEGIN;');
    }
    try {
      for (const change of changes) {
        const applied = await this.applyChange(change);
        if (applied) {
          appliedCount++;

          // Track changes by table for broadcast
          const key = change.table;
          if (!changesByTable.has(key)) {
            changesByTable.set(key, { operation: change.operation, rowIds: [] });
          }
          changesByTable.get(key)!.rowIds.push(change.row_id);
        }
      }
      if (useTransaction) {
        await this.db.exec('COMMIT;');
      }
    } catch (error) {
      if (useTransaction) {
        try {
          await this.db.exec('ROLLBACK;');
        } catch {}
      }
      throw error;
    }

    if (appliedCount > 0) {
      const elapsed = (performance.now() - startTime).toFixed(0);
      this.log.info(`Applied ${appliedCount}/${changes.length} changes in ${elapsed}ms`);

      // Notify reactive queries for each affected table so the UI refreshes
      changesByTable.forEach((_value, table) => {
        this.db.notifyDataChange(table);
      });

      // Broadcast to followers (only if this tab is leader)
      this.broadcastDataChanges(changesByTable);
    }

    return appliedCount;
  }

  /**
   * Broadcast data changes to follower tabs
   */
  private broadcastDataChanges(
    changesByTable: Map<string, { operation: string; rowIds: string[] }>,
  ): void {
    // Lazy-inject CustomSyncService to avoid circular dependency
    if (!this.syncService) {
      try {
        // Use injector to get CustomSyncService lazily
        // Import here to avoid circular dependency at module load time
        const { CustomSyncService } = require('./custom-sync.service');
        this.syncService = this.injector.get(CustomSyncService);
      } catch (error) {
        // Sync service not available yet (during bootstrap) - skip broadcast
        this.log.debug('CustomSyncService not available for broadcasting');
        return;
      }
    }

    changesByTable.forEach((value, table) => {
      this.syncService?.broadcastDataChange(table, value.operation, value.rowIds);
    });
  }

  /**
   * Apply a single change with conflict resolution
   */
  private async applyChange(change: ChangeRecord): Promise<boolean> {
    const pkCol = change.pk_col ?? 'id';

    // Never apply changes to SQLite-internal tables (sqlite_sequence, sqlite_stat1, etc.).
    // These have non-standard schemas and are never part of the application sync protocol.
    if (change.table.startsWith('sqlite_')) {
      this.log.warn(`Skipping change for SQLite-internal table: ${change.table}`);
      return false;
    }

    try {
      // Conflict resolution: check if local version is newer when table tracks updated_at.
      // Some tables (for example sheet_formats/label_layouts) do not define updated_at.
      // form_blobs is content-addressed (immutable, no updated_at/id columns) — skip conflict check.
      const tableHasUpdatedAt = await this.hasUpdatedAtColumn(change.table);
      if (change.operation !== 'DELETE' && change.table !== 'form_blobs' && tableHasUpdatedAt) {
        const existing = await this.db.get<{ updated_at: number }>(
          `SELECT updated_at FROM ${change.table} WHERE ${pkCol} = ?`,
          [change.row_id],
        );

        if (existing && existing.updated_at > change.timestamp) {
          // Local version is newer - reject incoming change
          this.log.warn(`Conflict: local version newer for ${change.table}.${change.row_id}`, {
            local_time: existing.updated_at,
            incoming_time: change.timestamp,
            diff_ms: existing.updated_at - change.timestamp,
          });

          // If difference is suspiciously large (>24h), log for investigation
          if (Math.abs(existing.updated_at - change.timestamp) > 86400000) {
            this.log.error(`Extreme timestamp difference detected - possible clock sync issue`);
          }

          return false; // Skip this change
        }
      }

      // Apply the change
      if (change.operation === 'DELETE') {
        await this.db.exec(`DELETE FROM ${change.table} WHERE ${pkCol} = ?`, [change.row_id]);
        this.log.debug(`Deleted ${change.table}.${change.row_id}`);
      } else if (change.operation === 'INSERT') {
        await this.insertRow(change.table, change.data!);
        this.log.debug(`Inserted ${change.table}.${change.row_id}`);
      } else {
        // UPDATE
        await this.updateRow(change.table, pkCol, change.row_id, change.data!);
        this.log.debug(`Updated ${change.table}.${change.row_id}`);
      }

      return true;
    } catch (error: any) {
      this.log.error(`Failed to apply ${change.operation} on ${change.table}:`, error);
      return false;
    }
  }

  private async hasUpdatedAtColumn(table: string): Promise<boolean> {
    const cached = this.hasUpdatedAtColumnCache.get(table);
    if (cached !== undefined) {
      return cached;
    }

    try {
      const columns = (await this.db.exec(`PRAGMA table_info(${table})`)) as Array<{
        name?: string;
      }>;
      const hasUpdatedAt = (columns ?? []).some((column) => column.name === 'updated_at');
      this.hasUpdatedAtColumnCache.set(table, hasUpdatedAt);
      return hasUpdatedAt;
    } catch {
      // Keep previous behavior when table metadata cannot be inspected.
      return true;
    }
  }

  /**
   * Insert a row
   * CRITICAL: Use INSERT ON CONFLICT DO NOTHING instead of INSERT OR REPLACE
   * INSERT OR REPLACE causes endless loops with reactive queries because:
   * 1. REPLACE deletes + reinserts, updating updated_at timestamp
   * 2. Reactive queries detect the change and re-execute
   * 3. Re-execution checks for newer rows and finds the same data
   * 4. INSERT OR REPLACE executes again -> infinite loop
   *
   * Using INSERT ON CONFLICT DO NOTHING prevents this because:
   * - First insert succeeds and sets data
   * - Subsequent inserts are no-ops (no timestamp change)
   * - No reactive query trigger -> loop breaks
   */
  private async insertRow(table: string, data: Record<string, any>): Promise<void> {
    const columns = Object.keys(data);
    const placeholders = columns.map(() => '?').join(', ');
    const values = Object.values(data);

    // Use INSERT OR IGNORE to prevent endless reactive query loops (same as ON CONFLICT DO NOTHING
    // but also handles UNIQUE constraints beyond the PK, e.g. unique pair constraints).
    // Do NOT use INSERT OR REPLACE — it deletes+reinserts, updating updated_at and re-triggering
    // reactive queries, causing infinite refresh loops.
    const sql = `INSERT OR IGNORE INTO ${table} (${columns.join(', ')}) VALUES (${placeholders})`;

    // exec() internally uses execO which implements prepare/bind/step pattern
    await this.db.exec(sql, values);
  }

  /**
   * Update a row
   */
  private async updateRow(
    table: string,
    pkCol: string,
    rowId: string,
    data: Record<string, any>,
  ): Promise<void> {
    const columns = Object.keys(data);
    const values = Object.values(data);
    const setClauses = columns.map((col) => `${col} = ?`).join(', ');

    // exec() internally uses execO which implements prepare/bind/step pattern
    await this.db.exec(`UPDATE ${table} SET ${setClauses} WHERE ${pkCol} = ?`, [...values, rowId]);
  }
}
